// AssistantPhotosPanel — Jul 2026 (Randy). Content for the AI Assistant page's
// right "Photos" edge tab (was a "coming soon" blank). Shows every image the
// silo owns — cube faces + silo art — newest first, with a tap-to-enlarge view.
import { useEffect, useState } from "react";
import { Image as ImageIcon, X, ArrowSquareOut } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { LABELS } from "@/lib/siloCubeFaces";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SOURCE_LABEL = { ai: "AI generated", generate: "AI generated", upload: "Uploaded", restore: "Restored" };

export default function AssistantPhotosPanel({ silo }) {
  const [images, setImages] = useState(null);
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!silo?.id) return;
    api.get(`/silos/${silo.id}/images`)
      .then((r) => setImages(r.data.images || []))
      .catch(() => setImages([]));
  }, [silo?.id]);

  if (images === null) {
    return <div className="h-full flex items-center justify-center text-sm text-[#9b9a96]">Loading photos…</div>;
  }
  if (images.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8 gap-2" data-testid="assistant-photos-empty">
        <ImageIcon size={28} weight="duotone" className="text-[#B5B3AE]" />
        <div className="text-sm text-[#6B6A68]">No photos yet.</div>
        <div className="text-xs text-[#9b9a96] leading-relaxed max-w-[220px]">
          Images you generate or upload for this silo's art &amp; cube faces will collect here.
        </div>
      </div>
    );
  }

  const url = (id) => `${BACKEND_URL}/api/silo-images/${id}`;
  return (
    <div className="h-full overflow-y-auto p-3" data-testid="assistant-photos-panel">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-2">
        {images.length} photo{images.length === 1 ? "" : "s"} · this silo's art &amp; faces
      </div>
      <div className="grid grid-cols-2 gap-2">
        {images.map((img) => (
          <button key={img.id} onClick={() => setZoom(img)} data-testid={`assistant-photo-${img.id}`}
            className="group relative rounded-lg overflow-hidden border hairline bg-[#F0F1EE] aspect-square text-left">
            <img src={url(img.id)} alt={img.prompt || img.face || "silo image"} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-200" />
            <div className="absolute inset-x-0 bottom-0 px-1.5 py-1 bg-gradient-to-t from-black/60 to-transparent">
              <div className="text-[9px] text-white truncate">
                {LABELS[img.face] || img.face || "Image"}{img.in_use ? " · in use" : ""}
              </div>
            </div>
          </button>
        ))}
      </div>

      {zoom && (
        <div className="fixed inset-0 z-[220] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setZoom(null)} data-testid="assistant-photo-zoom">
          <div className="relative max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={url(zoom.id)} alt="" className="w-full max-h-[75dvh] object-contain rounded-lg" />
            <div className="mt-2 flex items-center gap-2 text-xs text-white/85">
              <span className="flex-1 truncate">
                {LABELS[zoom.face] || zoom.face || "Image"} · {SOURCE_LABEL[zoom.source] || zoom.source || ""}
                {zoom.created_at ? ` · ${String(zoom.created_at).slice(0, 10)}` : ""}
              </span>
              <a href={url(zoom.id)} target="_blank" rel="noopener noreferrer" data-testid="assistant-photo-open-full"
                className="flex items-center gap-1 underline hover:text-white shrink-0"><ArrowSquareOut size={12} /> Full size</a>
              <button onClick={() => setZoom(null)} data-testid="assistant-photo-zoom-close"
                className="p-1.5 rounded-md bg-white/15 hover:bg-white/25 shrink-0"><X size={14} weight="bold" /></button>
            </div>
            {zoom.prompt && <div className="mt-1 text-[10px] text-white/60 leading-relaxed line-clamp-3">"{zoom.prompt}"</div>}
          </div>
        </div>
      )}
    </div>
  );
}
